import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField } from './shared';

const PercentageCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [p, setP] = useState('10,00');
  const [v, setV] = useState('200,00');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const pVal = parseFloat(parseBrazilianNumber(p)) || 0;
    const vVal = parseFloat(parseBrazilianNumber(v)) || 0;
    const res = (pVal / 100) * vVal;

    const hasResult = p && v && !isNaN(res) && isFinite(res);

    if (hasResult) {
      setResult({ res, pVal, vVal });
      onResultChange([
        { label: 'Porcentagem', value: `${pVal}%` },
        { label: 'Valor Base', value: `R$ ${vVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
        { label: 'Resultado', value: `R$ ${res.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
      ]);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-purple-50 to-slate-50 rounded-full border border-purple-200 mb-4">
          <span className="text-2xl">📊</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">Porcentagem</span>
        </div>
        <p className="text-theme-muted text-sm">Calcule valores percentuais rapidamente</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Porcentagem (%)"
            placeholder="Ex: 10,00"
            value={p}
            onChange={setP}
            required
            isNumeric
          />

          <FormField
            label="Valor (R$)"
            placeholder="Ex: 200,00"
            value={v}
            onChange={setV}
            required
            isNumeric
          />
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calculate}
          className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
        >
          <Icon name="play" className="w-5 h-5" />
          Calcular Porcentagem
        </button>
      </div>

      {result && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-purple-500 via-fuchsia-500 to-pink-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-purple-500 to-fuchsia-500 border-fuchsia-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">🎯</div>
            </div>

            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Resultado</p>
              <p className="text-3xl font-black mb-2">R$ {result.res.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</p>
              <div className="w-20 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Cálculo</p>
                <p className="text-2xl font-black">{result.pVal}%</p>
                <p className="text-xs opacity-70 mt-1">de R$ {result.vVal}</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Fórmula</p>
                <p className="text-2xl font-black">(p ÷ 100) × v</p>
                <p className="text-xs opacity-70 mt-1">aplicada</p>
              </div>
            </div>

            <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📌 Observação</p>
              <p className="text-sm font-medium">Use sinal negativo para descontos (ex: -10%)</p>
            </div>
          </div>
        </div>
      )}

      {!result && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">📊</div>
          <p className="font-bold text-base">Preencha os dados e clique em Calcular</p>
        </div>
      )}

      {/* Exemplos Práticos */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-3xl border border-purple-200">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-xl">
              <span className="text-2xl">💡</span>
            </div>
          </div>
          <h3 className="text-lg font-black text-theme-text mb-2">Exemplos Práticos</h3>
          <p className="text-sm text-theme-muted">Aplicações comuns do cálculo de porcentagem</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-theme-result p-4 rounded-2xl border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🛍️</span>
              <span className="text-sm font-black text-theme-text">Desconto</span>
            </div>
            <p className="text-xs text-theme-muted">20% de R$ 150 = R$ 30 de desconto</p>
          </div>
          <div className="bg-theme-result p-4 rounded-2xl border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📈</span>
              <span className="text-sm font-black text-theme-text">Aumento</span>
            </div>
            <p className="text-xs text-theme-muted">15% de R$ 200 = R$ 30 de aumento</p>
          </div>
          <div className="bg-theme-result p-4 rounded-2xl border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🎯</span>
              <span className="text-sm font-black text-theme-text">Meta</span>
            </div>
            <p className="text-xs text-theme-muted">80% de 50 vendas = 40 vendas alcançadas</p>
          </div>
        </div>
      </div>

      {/* Dicas Adicionais */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">💡</span>
            <span className="text-sm font-black text-theme-text">Dica Rápida</span>
          </div>
          <p className="text-xs text-theme-muted">10% = dividir por 10 | 25% = dividir por 4 | 50% = dividir por 2</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📊</span>
            <span className="text-sm font-black text-theme-text">Precisão</span>
          </div>
          <p className="text-xs text-theme-muted">Use casas decimais para porcentagens exatas (ex: 12,5%)</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🎯</span>
            <span className="text-sm font-black text-theme-text">Aplicação</span>
          </div>
          <p className="text-xs text-theme-muted">Essencial para finanças, vendas e estatísticas do dia a dia</p>
        </div>
      </div>
    </div>
  );
};

export default PercentageCalculator;
